import {useEffect, useRef} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';
import {
  BodySite,
  DiagnosticReportId,
  ScoringSystemItem,
  Pathology,
  QuestionnaireAccordion,
  ScoringSystem
} from '@legit-health/ui';
import SendIcon from '@mui/icons-material/Send';
import {Alert, Box, Button} from '@mui/material';
import {TFunction} from 'i18next';
import {useForm} from 'react-hook-form';
import * as Yup from 'yup';
import {useSharedDataContext} from '@/shared/components/layout/LoggedInLayout/contexts/SharedDataContext';
import {definedStringSchema} from '@/shared/utils/yup';
import BottomBar from '../../../../steps/BottomBar';
import createScoringSystemSchema from '../../../../utils/createScoringSystemSchema';
import {processScoringSystems} from '../../../../utils/mapFormValuesToApiFormat';
import ScoringSystemsSkeleton from './ScoringSystemsSkeleton';
import usePatchDiagnosisSupportReport from './usePatchDiagnosisSupportReport';

export default function ScoringSystems({
  activeStep,
  bodySiteId,
  diagnosticReportId,
  onFinish,
  renderItemLabel,
  pathology,
  t,
  totalSteps
}: {
  activeStep: number;
  bodySiteId: string;
  diagnosticReportId: DiagnosticReportId;
  onFinish: () => void;
  renderItemLabel?: (options: {
    item: ScoringSystemItem;
    scoringSystem: ScoringSystem;
    bodySite: BodySite;
  }) => string;
  pathology: Pathology;
  t: TFunction;
  totalSteps: number;
}) {
  const isSending = useRef(false);
  const {bodySitesById, getQuestionnairesForPathology} = useSharedDataContext();
  const scoringSystems = getQuestionnairesForPathology(pathology);

  const {
    patch: patchDiagnosticReport,
    isError,
    isPending
  } = usePatchDiagnosisSupportReport({
    onSuccess: () => onFinish()
  });
  const form = useForm({
    defaultValues: {
      diagnosticReportId,
      scoringSystems: scoringSystems.reduce(
        (acc, scoringSystem) => ({
          ...acc,
          [scoringSystem.id]: {}
        }),
        {}
      )
    },
    resolver: yupResolver(
      Yup.object({
        diagnosticReportId: definedStringSchema,
        scoringSystems: createScoringSystemSchema(scoringSystems)
      })
    )
  });
  const bodySite = bodySitesById[parseInt(bodySiteId)] as BodySite;

  function handleSubmit(formValues: {
    diagnosticReportId: DiagnosticReportId;
    scoringSystems: Record<string, Record<string, string>>;
  }) {
    if (!formValues.diagnosticReportId) {
      return;
    }
    patchDiagnosticReport({
      diagnosticReportId: formValues.diagnosticReportId,
      data: {
        scoringSystems: processScoringSystems(formValues.scoringSystems)
      }
    });
  }

  useEffect(
    function () {
      if (isSending.current) {
        return;
      }
      if (scoringSystems.length === 0) {
        isSending.current = true;
        /**
         * @see https://github.com/TanStack/query/issues/5341
         */
        setTimeout(
          () =>
            patchDiagnosticReport({
              diagnosticReportId,
              data: {
                scoringSystems: []
              }
            }),
          0
        );
      }
    },
    [scoringSystems, onFinish, diagnosticReportId, patchDiagnosticReport]
  );

  return (
    <Box
      sx={{
        maxWidth: 'md',
        mx: 'auto'
      }}
    >
      {scoringSystems.length === 0 && <ScoringSystemsSkeleton />}
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div>
          {scoringSystems.map((scoringSystem) => (
            <div key={scoringSystem.id}>
              <QuestionnaireAccordion
                form={form}
                scoringSystem={scoringSystem}
                renderQuestion={
                  renderItemLabel
                    ? (item) => renderItemLabel({item, scoringSystem, bodySite})
                    : undefined
                }
              />
            </div>
          ))}
        </div>
        <BottomBar
          activeStep={activeStep}
          totalSteps={totalSteps}
          isBackEnabled={false}
          rightButton={
            <Button
              color="secondary"
              disableElevation
              disabled={!form.formState.isValid || isPending}
              size="medium"
              variant="contained"
              endIcon={<SendIcon />}
              type="submit"
              data-testid="stepperNextButton"
            >
              {isPending ? t('ui:sending') : t('ui:send')}
            </Button>
          }
          t={t}
        />
      </form>
      {isError && (
        <Box
          sx={{
            mt: 2
          }}
        >
          <Alert severity="error">{t('shared/common:diagnosticReport.form.serverError')}</Alert>
        </Box>
      )}
    </Box>
  );
}
